import { ActionCreator } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ActivityApi } from '../../../api/projectActivityApi';
import { ProjectsApi } from '../../../api/projectApi';
import { toastMsg } from '../../../components/Toast/toastMsg';
import { IPagination } from '../../../types/pagination';
import { IProjectActivity, IProjectActivitySorting } from '..//types/projectActivity';
import { ProjectsState } from '../reducers';
import { IActivityFilter, IActivityFilterOption, IActivitySearch } from '../types/activityFilters';
import { ILabelMergeTasksBulk } from '../types/project';
import { IDirectCost } from './../types/directAndPassTroughCosts';
import { IProjectActivityHistory } from './../types/projectActivity';
import * as constants from './constants';

export type ProjectActivityThunkResult<R> = ThunkAction<Promise<R>, ProjectsState, null, ActivitiesActions>
export type ProjectActivityThunkDispatchType = ThunkDispatch<ProjectsState, null, ActivitiesActions>

export type ActivitiesThunkResult<R> = ThunkAction<Promise<R>, ProjectsState, null, ActivitiesActions>
export type ActivitiesThunkDispatchType = ThunkDispatch<ProjectsState, null, ActivitiesActions>

export interface ActivitiesHasErrored {
  type: constants.ACTIVITIES_HAS_ERRORED
  hasErrored: boolean
}

export interface GetActivities {
  type: constants.GET_ACTIVITIES
  filters: string
}

export interface GetActivitiesSuccess {
  type: constants.GET_ACTIVITIES_SUCCESS
  activities: IProjectActivity[]
}

export interface GetPaginatedActivitiesSuccess {
  type: constants.GET_PAGINATED_ACTIVITIES_SUCCESS
  activities: IPagination<IProjectActivity[]>
}

export interface SetPaginatedActivitiesActivePage {
  type: constants.SET_PAGINATED_ACTIVITIES_ACTIVE_PAGE
  page: number
}

export interface SearchActivities {
  type: constants.SEARCH_ACTIVITIES
  filter: IActivitySearch
}

export interface SortActs {
  type: constants.SORT_ACTS
  sorting: IProjectActivitySorting
}

export interface SortActsInDanger {
  type: constants.SORT_ACTS_IN_DANGER
  sorting: IProjectActivitySorting
}

export interface ActivityHasErrored {
  type: constants.ACTIVITY_HAS_ERRORED
  hasErrored: boolean
}

export interface GetActivitySuccess {
  type: constants.GET_ACTIVITY_SUCCESS
  activity: IProjectActivity
}

export interface UpdateActivitySuccess {
  type: constants.UPDATE_ACTIVITY_SUCCESS
  activity: IProjectActivity
}

export interface CreateActivitySuccess {
  type: constants.CREATE_ACTIVITY_SUCCESS
  activity: IProjectActivity
}

export interface SetActFilters {
  type: constants.SET_ACT_FILTERS
  actFilter: IActivityFilter
  queryOptions: IActivityFilterOption
}

export interface GetActFiltersSuccess {
  type: constants.GET_ACT_FILTERS_SUCCESS
  filters: IActivityFilter[]
}

export interface ClearActFilters {
  type: constants.CLEAR_ACT_FILTERS
}

export interface SetSelectedActivity {
  type: constants.SET_SELECTED_ACTIVITY
  activity: IProjectActivity
}

export interface GetActivityHistorySuccess {
  type: constants.GET_ACT_HISTORY_SUCCESS
  history: IProjectActivityHistory[]
}

export interface ResetActivityHistory {
  type: constants.RESET_ACT_HISTORY
}

export interface DeleteActivitySuccess {
  type: constants.DELETE_ACTIVITY_SUCCESS
  activity: IProjectActivity
}

export interface SetOpenActDetailsModal {
  type: constants.SET_OPEN_ACT_DETAILS_MODAL
  open: boolean
}

export interface ProjectLabelMergeTasksSuccess {
  type: constants.PROJECT_LABELS_MERGE_TASKS_SUCCESS
  activities: IProjectActivity[]
}

export interface SetDraggableIndicatorActSuccess {
  type: constants.SET_DRAGGABLE_INDICATOR_ACT_SUCCESS
  draggable: boolean
}

export const activitiesHasErrored = (hasErrored: boolean, error: Error, action?: string): ActivitiesHasErrored => {
  if (error && action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.ACTIVITIES_HAS_ERRORED,
    hasErrored: hasErrored
  }
}

export const getActivities: ActionCreator<ActivitiesThunkResult<IProjectActivity[]>>
  = (id: string | number, queryParams?: Record<string, string>): ActivitiesThunkResult<IProjectActivity[]> => {
    return async (dispatch: ActivitiesThunkDispatchType) => {
      return ProjectsApi.getActivities(id, queryParams)
        .then(response => {
          dispatch(getActivitiesSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(activitiesHasErrored(true, error.response.data, constants.GET_ACTIVITIES))
          return {} as IProjectActivity[]
        })
    }
  }


export const getActivitiesSuccess = (activities: IProjectActivity[]): GetActivitiesSuccess => {
  return {
    type: constants.GET_ACTIVITIES_SUCCESS,
    activities
  }
}

export const getPaginatedActivities: ActionCreator<ActivitiesThunkResult<IPagination<IProjectActivity[]>>>
  = (id: string | number, queryParams: Record<string, string>): ActivitiesThunkResult<IPagination<IProjectActivity[]>> => {
    return async (dispatch: ActivitiesThunkDispatchType) => {
      return ProjectsApi.getPaginatedActivities(id, queryParams)
        .then(response => {
          dispatch(getPaginatedActivitiesSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(activitiesHasErrored(true, error.response.data, constants.GET_PAGINATED_ACTIVITIES))
          return {} as IPagination<IProjectActivity[]>
        })
    }
  }


export const getPaginatedActivitiesSuccess = (activities: IPagination<IProjectActivity[]>): GetPaginatedActivitiesSuccess => {
  return {
    type: constants.GET_PAGINATED_ACTIVITIES_SUCCESS,
    activities
  }
}

export const setPaginatedActivitiesActivePage = (page: number): SetPaginatedActivitiesActivePage => {
  return {
    type: constants.SET_PAGINATED_ACTIVITIES_ACTIVE_PAGE,
    page: page
  }
}

export const searchActivities = (filter: IActivitySearch): SearchActivities => {
  return {
    type: constants.SEARCH_ACTIVITIES,
    filter
  }
}

export const sortActs = (sorting: IProjectActivitySorting): SortActs => {
  return {
    type: constants.SORT_ACTS,
    sorting
  }
}

export const sortActsInDanger = (sorting: IProjectActivitySorting): SortActsInDanger => {
  return {
    type: constants.SORT_ACTS_IN_DANGER,
    sorting
  }
}

export const activityHasErrored = (hasErrored: boolean, error: Error, action?: string): ActivityHasErrored => {
  if (error && action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.ACTIVITY_HAS_ERRORED,
    hasErrored: hasErrored
  }
}

export const getActivity: ActionCreator<
  ProjectActivityThunkResult<IProjectActivity>
> = (projectId: string | number, activity: IProjectActivity): ProjectActivityThunkResult<IProjectActivity> => {
  return async (dispatch: ProjectActivityThunkDispatchType) => {
    return ActivityApi.getActivity(projectId, activity)
      .then(response => {
        dispatch(getActivitySuccess(response))
        return response
      })
      .catch(error => {
        dispatch(activityHasErrored(true, error.response.data, constants.GET_ACTIVITY))
        return {} as IProjectActivity
      })
  }
}

export const setActFilters = (actFilter: IActivityFilter, queryOptions: IActivityFilterOption): SetActFilters => {
  return {
    type: constants.SET_ACT_FILTERS,
    actFilter,
    queryOptions
  }
}

export const getActFilters: ActionCreator<ActivitiesThunkResult<IActivityFilter[]>>
  = (id: string | number): ActivitiesThunkResult<IActivityFilter[]> => {
    return async (dispatch: ActivitiesThunkDispatchType) => {
      return ProjectsApi.getActFilters(id)
        .then(response => {
          dispatch(getActFiltersSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(activitiesHasErrored(true, error.response.data, constants.GET_ACT_FILTERS))
          return [] as IActivityFilter[]
        })
    }
  }

export const getActivitySuccess = (activity: IProjectActivity): GetActivitySuccess => {
  return {
    type: constants.GET_ACTIVITY_SUCCESS,
    activity
  }
}

export const updateActivity: ActionCreator<
  ProjectActivityThunkResult<IProjectActivity>
> = (projectId: string | number, activity: IProjectActivity, changed?: string): ProjectActivityThunkResult<IProjectActivity> => {
  return async (dispatch: ProjectActivityThunkDispatchType) => {
    return ActivityApi.updateActivity(projectId, activity)
      .then(response => {
        dispatch(updateActivitySuccess(response, changed))
        return response
      })
      .catch(error => {
        dispatch(activityHasErrored(true, error.response.data, constants.UPDATE_ACTIVITY))
        return {} as IProjectActivity
      })
  }
}

export const updateActivitySuccess = (activity: IProjectActivity, changed?: string): UpdateActivitySuccess => {
  if (changed === 'completed') {
    toastMsg(`${activity.name} was marked as done.`, constants.UPDATE_ACTIVITY_SUCCESS, 'success')
  } else if (changed === 'in_progress') {
    toastMsg(`${activity.name} was marked as undone.`, constants.UPDATE_ACTIVITY_SUCCESS, 'success')
  } else {
    toastMsg(`Updated ${activity.name} data successfully.`, constants.UPDATE_ACTIVITY_SUCCESS, 'success')
  }
  return {
    type: constants.UPDATE_ACTIVITY_SUCCESS,
    activity
  }
}

export const getActFiltersSuccess = (filters: IActivityFilter[]): GetActFiltersSuccess => {
  return {
    type: constants.GET_ACT_FILTERS_SUCCESS,
    filters
  }
}

export const clearActFilters = (): ClearActFilters => {
  return {
    type: constants.CLEAR_ACT_FILTERS
  }
}

export const setSelectedActivity = (activity: IProjectActivity): SetSelectedActivity => {
  return {
    type: constants.SET_SELECTED_ACTIVITY,
    activity
  }
}

export const getActivityHistory: ActionCreator<
  ProjectActivityThunkResult<IProjectActivityHistory[]>
> = (projectId: string | number, activity: IProjectActivity, search: string): ProjectActivityThunkResult<IProjectActivityHistory[]> => {
  return async (dispatch: ProjectActivityThunkDispatchType) => {
    return ActivityApi.getActivityHistory(projectId, activity, search)
      .then(response => {
        dispatch(getActivityHistorySuccess(response))
        return response
      })
      .catch(error => {
        dispatch(activityHasErrored(true, error.response.data, constants.GET_ACT_HISTORY))
        return {} as IProjectActivityHistory[]
      })
  }
}


export const getActivityHistorySuccess = (history: IProjectActivityHistory[]): GetActivityHistorySuccess => {
  return {
    type: constants.GET_ACT_HISTORY_SUCCESS,
    history
  }
}

export const resetActivityHistory = (): ResetActivityHistory => {
  return {
    type: constants.RESET_ACT_HISTORY
  }
}

export const deleteActivity: ActionCreator<ProjectActivityThunkResult<string | number | void>
> = (projectId: string | number, activity: IProjectActivity): ProjectActivityThunkResult<string | number | void> => {
  return async (dispatch: ProjectActivityThunkDispatchType) => {
    return ActivityApi.deleteActivity(projectId, activity)
      .then(response => {
        dispatch(deleteActivitySuccess(activity))
        return response
      })
      .catch(error => {
        dispatch(activityHasErrored(true, error.response.data, constants.DELETE_ACTIVITY))
      })
  }
}

export const deleteActivitySuccess = (activity: IProjectActivity): DeleteActivitySuccess => {
  toastMsg(`Deleted ${activity.name} activity successfully.`, constants.DELETE_ACTIVITY_SUCCESS, 'success')
  return {
    type: constants.DELETE_ACTIVITY_SUCCESS,
    activity
  }
}

export const createActivity: ActionCreator<
  ProjectActivityThunkResult<IProjectActivity>
> = (projectId: string | number, activity: IProjectActivity): ProjectActivityThunkResult<IProjectActivity> => {
  return async (dispatch: ProjectActivityThunkDispatchType) => {
    return ActivityApi.createActivity(projectId, activity)
      .then(response => {
        dispatch(createActivitySuccess(response))
        return response
      })
      .catch(error => {
        dispatch(activityHasErrored(true, error.response.data, constants.CREATE_ACTIVITY))
        return {} as IProjectActivity
      })
  }
}

export const createActivitySuccess = (activity: IProjectActivity): CreateActivitySuccess => {
  toastMsg(`Create ${activity.name} activity successfully.`, constants.CREATE_ACTIVITY_SUCCESS, 'success')
  return {
    type: constants.CREATE_ACTIVITY_SUCCESS,
    activity
  }
}

export const setOpenActDetailsModal = (open: boolean): SetOpenActDetailsModal => {
  return {
    type: constants.SET_OPEN_ACT_DETAILS_MODAL,
    open
  }
}

export const mergeActivitiesWithLabel: ActionCreator<ProjectActivityThunkResult<IProjectActivity[] | IDirectCost[]>>
  = (projectId: string | number, activitiesBulk: ILabelMergeTasksBulk): ProjectActivityThunkResult<IProjectActivity[]> => {
    return async (dispatch: ActivitiesThunkDispatchType) => {
      return ProjectsApi.mergeProjectTaskWithLabels(projectId, activitiesBulk)
        .then(response => {
          dispatch(mergeActivitiesWithLabelSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(activitiesHasErrored(true, error.response.data, constants.PROJECT_LABELS_MERGE_TASKS))
          return {} as IProjectActivity[]
        })
    }
  }

export const mergeActivitiesWithLabelSuccess = (activities: IProjectActivity[]): ProjectLabelMergeTasksSuccess => {
  toastMsg(`Tasks successfully merged with label.`, constants.PROJECT_LABELS_MERGE_TASKS_SUCCESS, 'success')
  return {
    type: constants.PROJECT_LABELS_MERGE_TASKS_SUCCESS,
    activities
  }
}

export const setDraggableIndicatorAct = (draggable: boolean): SetDraggableIndicatorActSuccess => {
  return {
    type: constants.SET_DRAGGABLE_INDICATOR_ACT_SUCCESS,
    draggable
  }
}

export type ActivitiesActions
  = GetActivities
  | GetActivitiesSuccess
  | SearchActivities
  | SortActs
  | ActivityHasErrored
  | UpdateActivitySuccess
  | GetActivitySuccess
  | SetActFilters
  | ActivitiesHasErrored
  | GetActFiltersSuccess
  | ClearActFilters
  | SetSelectedActivity
  | GetActivityHistorySuccess
  | ResetActivityHistory
  | DeleteActivitySuccess
  | CreateActivitySuccess
  | SetOpenActDetailsModal
  | ProjectLabelMergeTasksSuccess
  | SortActsInDanger
  | SetDraggableIndicatorActSuccess
  | GetPaginatedActivitiesSuccess
  | SetPaginatedActivitiesActivePage
